import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import LoadingScreen from 'src/components/LoadingScreen';
import GuestGuard from 'src/components/Guards/GuestGuard';
import AuthGuard from 'src/components/Guards/AuthGuard';
import DashboardLayout from 'src/layouts/DashboardLayout';
import SettingsLayout from 'src/layouts/SettingsLayout';
import BuyCredit from './views/buyCredit';
import TransferCredit from './views/transferCredit';
import DocsLayout from 'src/documentation/DocsLayout';

const systemCompanyNameShort =
  JSON.parse(window.localStorage.getItem('systemCompanyNameShort')) || 'Vezeti';

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

export const routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    guard: AuthGuard,
    exact: true,
    path: '/app/templates/create-template',
    component: lazy(() => import('src/views/template/CreateTemplate'))
  },
  {
    guard: AuthGuard,
    exact: true,
    path: '/app/templates/edit-template/:id',
    component: lazy(() => import('src/views/template/EditTemplate'))
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/suspended',
        component: lazy(() => import('src/views/errors/SuspendUser'))
      },
      {
        exact: true,
        path: '/app/get-started',
        component: lazy(() => import('src/views/reports/GetStarted'))
      },
      {
        exact: true,
        path: '/app/dashboard',
        component: lazy(() => import('src/views/reports/DashboardView'))
      },
      {
        exact: true,
        path: '/app/voicemail',
        component: lazy(() => import('src/views/voicemailMsg'))
      },
      {
        exact: true,
        path: '/app/sip-account',
        component: lazy(() => import('src/views/sipAccount'))
      },
      {
        exact: true,
        path: `/app/${systemCompanyNameShort.toLowerCase()}-numbers`,
        component: lazy(() => import('src/views/vezetiNumber'))
      },
      {
        exact: true,
        path: `/app/${systemCompanyNameShort.toLowerCase()}-numbers-pricing`,
        component: lazy(() => import('src/views/vezetiNumber/Pricing'))
      },
      {
        exact: true,
        path: '/app/dashboard-info',
        component: lazy(() => import('src/views/dashboardInfo'))
      },
      {
        exact: true,
        path: '/app/templates',
        component: lazy(() => import('src/views/template/ListTemplates'))
      },
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/dashboard" />
      },
      {
        exact: true,
        path: '/app/user-calls-log',
        component: lazy(() => import('src/views/userCallsLog'))
      },
      {
        path: '/app/buy-credit',
        layout: BuyCredit,
        routes: [
          {
            exact: true,
            path: '/app/buy-credit',
            component: lazy(() =>
              import('src/views/buyCredit/Partials/Balance')
            )
          },
          {
            exact: true,
            path: '/app/buy-credit/history',
            component: lazy(() =>
              import('src/views/buyCredit/Partials/History')
            )
          }
        ]
      },
      {
        exact: true,
        path: '/app/conference-bridge',
        component: lazy(() => import('src/views/conferenceBridge'))
      },
      {
        exact: true,
        path: '/app/premium-conference-bridge',
        component: lazy(() =>
          import('src/views/conferenceBridge/PremiumConferenceBridge')
        )
      },
      {
        exact: true,
        path: '/app/rates',
        component: lazy(() => import('src/views/rates'))
      },
      {
        exact: true,
        path: '/app/pricing',
        component: lazy(() => import('src/views/sipAccount/pricing/index'))
      },
      {
        path: '/app/transfer-credit',
        layout: TransferCredit,
        routes: [
          {
            exact: true,
            path: '/app/transfer-credit',
            component: lazy(() =>
              import('src/views/transferCredit/TransferCard')
            )
          },
          {
            exact: true,
            path: '/app/transfer-credit/history',
            component: lazy(() => import('src/views/transferCredit/History'))
          }
        ]
      },
      {
        exact: true,
        path: ['/app/verify-caller', '/app/verify-caller/:id'],
        component: lazy(() => import('src/views/verifyCallerID'))
      },
      {
        exact: true,
        path: '/app/resources',
        component: lazy(() => import('src/views/resources'))
      },
      {
        exact: true,
        path: '/app/resources/time-group',
        component: lazy(() => import('src/views/resources/timeGroup'))
      },
      {
        exact: true,
        path: '/app/resources/time-conditions',
        component: lazy(() => import('src/views/resources/timeCondition'))
      },
      {
        exact: true,
        path: '/app/resources/audio-files',
        component: lazy(() => import('src/views/resources/audioFiles'))
      },
      {
        exact: true,
        path: '/app/resources/audio-files/trimmer',
        component: lazy(() =>
          import('src/views/resources/audioFiles/Partials/AudioTrimmer')
        )
      },
      {
        exact: true,
        path: '/app/resources/virtual-extensions',
        component: lazy(() => import('src/views/resources/virtualExtension'))
      },
      {
        exact: true,
        path: '/app/resources/ivr',
        component: lazy(() => import('src/views/resources/ivr'))
      },
      {
        exact: true,
        path: '/app/resources/voicemails',
        component: lazy(() => import('src/views/resources/voicemails'))
      },
      {
        exact: true,
        path: '/app/resources/ring-group',
        component: lazy(() => import('src/views/resources/ringGroups'))
      },
      {
        exact: true,
        path: '/app/contactus',
        component: lazy(() => import('src/views/support/contactUs'))
      },
      {
        exact: true,
        path: '/app/permissions',
        component: lazy(() => import('src/views/permission'))
      },
      {
        path: '/app/settings',
        layout: SettingsLayout,
        routes: [
          {
            exact: true,
            path: '/app/settings/profile',
            component: lazy(() => import('src/views/setting/profile'))
          },
          {
            exact: true,
            path: '/app/settings',
            component: () => <Redirect to="/app/settings/profile" />
          },
          {
            exact: true,
            path: '/app/settings/security',
            component: lazy(() => import('src/views/setting/security'))
          },
          {
            exact: true,
            path: '/app/settings/system-settings',
            component: lazy(() => import('src/views/setting/systemSettings'))
          },
          {
            exact: true,
            path: '/app/settings/audit',
            component: lazy(() => import('src/views/setting/audit'))
          }
        ]
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/admin',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/admin/customer',
        component: lazy(() => import('src/views/Admin/crm/Customer'))
      },
      {
        exact: true,
        path: '/admin/partner',
        component: lazy(() => import('src/views/Admin/crm/Partner'))
      },
      {
        exact: true,
        path: '/admin/DID',
        component: lazy(() => import('src/views/Admin/crm/DID'))
      },
      {
        exact: true,
        path: '/admin/service-income',
        component: lazy(() => import('src/views/Admin/crm/ServiceIncome'))
      },
      {
        exact: true,
        path: '/admin/funding',
        component: lazy(() => import('src/views/Admin/history/Funding'))
      },
      {
        exact: true,
        path: '/admin/call-rates',
        component: lazy(() => import('src/views/Admin/callRates'))
      },
      {
        exact: true,
        path: '/admin/dashboard',
        component: lazy(() => import('src/views/Admin/dashboard'))
      },
      {
        exact: true,
        path: '/admin',
        component: () => <Redirect to="/admin/dashboard" />
      }
    ]
  },
  {
    path: '/docs',
    layout: DocsLayout,
    routes: [
      {
        exact: true,
        path: '/docs/overview',
        component: lazy(() => import('src/documentation/docs/Overview'))
      },
      {
        exact: true,
        path: '/docs/getting-started',
        component: lazy(() =>
          import('src/documentation/docs/GettingStartedView')
        )
      },
      {
        exact: true,
        path: '/docs/features',
        component: lazy(() => import('src/documentation/docs/Features'))
      },
      {
        exact: true,
        path: '/docs/folders',
        component: lazy(() => import('src/documentation/docs/Directory'))
      },
      {
        exact: true,
        path: '/docs/api-calls',
        component: lazy(() => import('src/documentation/docs/APICallsView'))
      },
      {
        exact: true,
        path: '/docs/environment-variables',
        component: lazy(() =>
          import('src/documentation/docs/EnvironmentVariablesView')
        )
      }
    ]
  },
  {
    path: '*',
    routes: [
      {
        exact: true,
        guard: GuestGuard,
        path: '/forgot-password',
        component: lazy(() => import('src/views/auth/ForgotPasswordView'))
      },
      {
        exact: true,
        guard: GuestGuard,
        path: '/login',
        component: lazy(() => import('src/views/auth/LoginView'))
      },
      {
        exact: true,
        guard: GuestGuard,
        path: '/register',
        component: lazy(() => import('src/views/auth/RegisterView'))
      },
      {
        exact: true,
        guard: GuestGuard,
        path: '/resetpassword',
        component: lazy(() => import('src/views/auth/PasswordReset'))
      },
      {
        exact: true,
        path: '/',
        component: () => <Redirect to="/login" />
      },
      {
        exact: true,
        path: '/support',
        component: lazy(() => import('src/views/support'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];
export default routes;
